import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Star,
  EmojiEvents,
  Psychology,
  AutoAwesome,
} from '@mui/icons-material';
import { useTranslation } from '../hooks/useTranslation';

const About = () => {
  const { t, language } = useTranslation();

  const localized = (hi, en) => ({ hi, en });

  const getText = (value) => {
    if (typeof value === 'string') {
      return value;
    }

    if (value && typeof value === 'object') {
      if (value[language]) return value[language];
      if (value.en) return value.en;
      if (value.hi) return value.hi;
    }

    return '';
  };

  const pageCopy = {
    avatarAlt: localized(
      'आचार्य अभिषेक पांडेय - ज्योतिष विशेषज्ञ',
      'Acharya Abhishek Pandey - Astrologer'
    ),
    heroDescription: localized(
      'आचार्य अभिषेक पांडेय जी एक प्रसिद्ध ज्योतिषी और तंत्र विशेषज्ञ हैं जिनके पास 15+ वर्षों का व्यापक अनुभव है। उन्होंने काशी हिंदू विश्वविद्यालय से ज्योतिष शास्त्र की शिक्षा प्राप्त की है और विभिन्न गुरुकुलों से तंत्र-मंत्र की गहन जानकारी हासिल की है। उनका मुख्य उद्देश्य लोगों की समस्याओं का प्रामाणिक और वैज्ञानिक समाधान प्रदान करना है।',
      'Acharya Abhishek Pandey is a renowned astrologer and tantra expert with more than 15 years of extensive experience. He studied astrology at Banaras Hindu University and gained advanced knowledge of tantra from various traditional gurukuls. His core mission is to deliver authentic and practical solutions to people’s life challenges.'
    ),
    journeyTitle: localized('हमारी यात्रा', 'Our Journey'),
    missionBody: localized(
      'हमारा मिशन है प्राचीन वैदिक ज्ञान और तंत्र विद्या के माध्यम से लोगों की जीवन समस्याओं का प्रामाणिक समाधान प्रदान करना। हम मानते हैं कि सच्चा ज्ञान वही है जो मानव कल्याण में काम आए। हमारा उद्देश्य है कि हर व्यक्ति को सही मार्गदर्शन मिले और वे अपने जीवन में सफलता और खुशी प्राप्त कर सकें।',
      'Our mission is to offer genuine solutions rooted in ancient Vedic wisdom and tantric practices. We believe that true knowledge is the one that uplifts humanity, guiding every individual toward success and happiness through the right direction.'
    ),
    visionBody: localized(
      'हमारा विजन है एक ऐसे समाज का निर्माण करना जहाँ हर व्यक्ति को अपनी समस्याओं का वैज्ञानिक और आध्यात्मिक समाधान मिल सके। हम चाहते हैं कि ज्योतिष और तंत्र विद्या का सदुपयोग हो और लोग इसके सच्चे लाभ उठा सकें। हमारा लक्ष्य है कि आने वाली पीढ़ियों तक इस पवित्र ज्ञान को सही रूप में पहुंचाना।',
      'Our vision is to build a society where everyone can access scientific and spiritual remedies for their concerns. We aim to promote the responsible use of astrology and tantra so that people can truly benefit, preserving this sacred knowledge for future generations.'
    ),
    stats: {
      experience: localized('वर्षों का अनुभव', 'Years of Experience'),
      clients: localized('संतुष्ट ग्राहक', 'Happy Clients'),
      services: localized('विभिन्न सेवाएं', 'Services Offered'),
      availability: localized('उपलब्धता', 'Availability'),
    },
  };

  const achievements = [
    {
      year: '2008',
      title: localized('ज्योतिष शास्त्र में प्रवीणता', 'Mastery in Astrology'),
      description: localized(
        'काशी हिंदू विश्वविद्यालय से ज्योतिष शास्त्र में स्नातक',
        'Graduated in Astrology from Banaras Hindu University'
      ),
    },
    {
      year: '2012',
      title: localized('तंत्र विद्या में विशेषज्ञता', 'Advanced Tantra Studies'),
      description: localized(
        'गुरुकुल से तंत्र-मंत्र की शिक्षा पूर्ण की',
        'Completed traditional tantra and mantra training at Gurukul'
      ),
    },
    {
      year: '2015',
      title: localized('व्यावसायिक सेवा प्रारंभ', 'Launched Professional Services'),
      description: localized(
        'जनसेवा हेतु ज्योतिष और तंत्र परामर्श शुरू किया',
        'Began offering astrology and tantra consultations for the community'
      ),
    },
    {
      year: '2020',
      title: localized('10,000+ संतुष्ट ग्राहक', '10,000+ Happy Clients'),
      description: localized(
        'सफलतापूर्वक हजारों लोगों की समस्याओं का समाधान',
        'Successfully resolved concerns for thousands of individuals'
      ),
    },
    {
      year: '2024',
      title: localized('डिजिटल सेवा विस्तार', 'Digital Service Expansion'),
      description: localized(
        'ऑनलाइन परामर्श और व्यापक जन सेवा',
        'Expanded to online consultations to reach a wider audience'
      ),
    },
  ];

  const expertise = [
    {
      icon: <Psychology />,
      title: localized('वैदिक ज्योतिष', 'Vedic Astrology'),
      description: localized(
        'कुंडली विश्लेषण, भविष्यवाणी और उपाय सुझाव में 15+ वर्षों का अनुभव',
        '15+ years of experience in horoscope analysis, predictions, and remedial guidance'
      ),
    },
    {
      icon: <AutoAwesome />,
      title: localized('तंत्र-मंत्र विद्या', 'Tantra & Mantra Expertise'),
      description: localized(
        'काला जादू निवारण, वशीकरण और सुरक्षा कवच में विशेषज्ञता',
        'Specialised in dispelling dark rituals, attraction practices, and protective shields'
      ),
    },
    {
      icon: <Star />,
      title: localized('रत्न चिकित्सा', 'Gem Therapy'),
      description: localized(
        'प्रमाणित रत्नों का चयन और ग्रह शांति के उपाय',
        'Curated, certified gemstones with remedies for planetary harmony'
      ),
    },
    {
      icon: <EmojiEvents />,
      title: localized('पूजा विधान', 'Ritual & Puja Services'),
      description: localized(
        'सभी प्रकार की पूजा-पाठ और धार्मिक अनुष्ठान',
        'Complete range of puja ceremonies and spiritual rituals'
      ),
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #FF6B35 0%, #1976D2 100%)',
          color: 'white',
          py: 8,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  sx={{
                    width: { xs: 200, md: 250 },
                    height: { xs: 200, md: 250 },
                    mx: 'auto',
                    mb: 2,
                    border: '4px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                    '& img': {
                      objectFit: 'cover',
                      objectPosition: 'center 20%',
                      width: '100%',
                      height: '100%',
                    },
                  }}
                  src="/images/acharya-abhishek-pandey.jpeg"
                  alt={getText(pageCopy.avatarAlt)}
                />
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography
                variant="h2"
                gutterBottom
                sx={{ fontWeight: 700, mb: 2 }}
              >
                {t('home.title')}
              </Typography>
              
              <Typography
                variant="h5"
                sx={{ mb: 3, opacity: 0.9 }}
              >
                {t('home.subtitle')}
              </Typography>
              
              <Typography
                variant="body1"
                sx={{ fontSize: '1.1rem', lineHeight: 1.8, opacity: 0.9 }}
              >
                {getText(pageCopy.heroDescription)}
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Expertise Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          align="center"
          gutterBottom
          sx={{ fontWeight: 600, mb: 6 }}
        >
          {t('about.expertise')}
        </Typography>
        
        <Grid container spacing={4} justifyContent="center">
          {expertise.map((item, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  p: 2,
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      p: 2,
                      borderRadius: '50%',
                      backgroundColor: 'primary.light',
                      color: 'white',
                      mb: 2,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600, color: 'primary.main' }}
                  >
                    {getText(item.title)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.6 }}
                  >
                    {getText(item.description)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Journey Timeline */}
      <Box sx={{ backgroundColor: 'background.default', py: 8 }}>
        <Container maxWidth="md">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ fontWeight: 600, mb: 6 }}
          >
            {getText(pageCopy.journeyTitle)}
          </Typography>
          
          <Grid container spacing={3} justifyContent="center" alignItems="stretch" sx={{ mt: 4 }}>
            {achievements.map((achievement, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }} key={index} sx={{ mt: 2 }}>
                <Card 
                  elevation={3} 
                  sx={{ 
                    height: '100%', 
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 180,
                    mt: 3,
                    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: 'primary.main',
                      color: 'white',
                      borderRadius: '50%',
                      width: 50,
                      height: 50,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 1,
                    }}
                  >
                    {achievement.year}
                  </Box>
                  <CardContent sx={{ pt: 5, pb: 3, px: 3, textAlign: 'center', flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{ fontWeight: 600, color: 'primary.main', mb: 2, fontSize: '1.1rem' }}
                    >
                      {getText(achievement.title)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {getText(achievement.description)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Mission & Vision */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} justifyContent="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: '100%', p: 3 }}>
              <CardContent>
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}
                >
                  {t('about.mission')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ lineHeight: 1.8, color: 'text.secondary' }}
                >
                  {getText(pageCopy.missionBody)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }} justifyContent="center">
            <Card sx={{ height: '100%', p: 3 }}>
              <CardContent>
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'secondary.main', mb: 3 }}
                >
                  {t('about.vision')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ lineHeight: 1.8, color: 'text.secondary' }}
                >
                  {getText(pageCopy.visionBody)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Statistics */}
      <Box sx={{ backgroundColor: 'primary.main', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} sx={{ textAlign: 'center' }} justifyContent="center">
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="h2" sx={{ fontWeight: 700, mb: 1 }}>
                15+
              </Typography>
              <Typography variant="h6">
                {getText(pageCopy.stats.experience)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="h2" sx={{ fontWeight: 700, mb: 1 }}>
                10,000+
              </Typography>
              <Typography variant="h6">
                {getText(pageCopy.stats.clients)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="h2" sx={{ fontWeight: 700, mb: 1 }}>
                50+
              </Typography>
              <Typography variant="h6">
                {getText(pageCopy.stats.services)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="h2" sx={{ fontWeight: 700, mb: 1 }}>
                24/7
              </Typography>
              <Typography variant="h6">
                {getText(pageCopy.stats.availability)}
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default About;